import { useFrame, useThree } from "@react-three/fiber";
import { memo, useRef, type MutableRefObject } from "react";
import { FLOOR_HEIGHT } from "../constants/camera";

/** Порог для прекращения lerp-анимации (в единицах мира) */
const LERP_THRESHOLD = 0.001;

const FloorCameraController = memo(function FloorCameraController({
  controlsRef,
  floor = 1,
  isCameraAnimating = false,
}: {
  controlsRef: MutableRefObject<any>;
  floor: number;
  isCameraAnimating?: boolean;
}) {
  const { camera, invalidate } = useThree();

  // Отслеживание состояния анимации для оптимизации
  const isLerping = useRef(false);
  const prevFloor = useRef(floor);

  useFrame(() => {
    // Отключаемся во время анимации камеры (RouteCameraController)
    if (isCameraAnimating) return;

    const controls = controlsRef.current;
    if (!controls) return;

    const targetY = (floor - 1) * FLOOR_HEIGHT;
    const currentTarget = controls.target;
    const delta = Math.abs(targetY - currentTarget.y);

    // Детектим смену этажа для запуска анимации
    if (prevFloor.current !== floor) {
      isLerping.current = true;
      prevFloor.current = floor;
    }

    // Если достигли цели - прекращаем lerp
    if (delta < LERP_THRESHOLD) {
      if (isLerping.current) {
        // Финальная установка точного значения
        currentTarget.y = targetY;
        controls.update?.();
        invalidate();
        isLerping.current = false;
      }
      return; // Ничего не делаем если уже на месте
    }

    // Выполняем lerp
    currentTarget.y += (targetY - currentTarget.y) * 0.1;

    // Также плавно поднимаем камеру, сохраняя относительное расстояние
    const targetCameraY = camera.position.y + (targetY - currentTarget.y) * 0.1;
    camera.position.y = targetCameraY;

    controls.update?.();
    invalidate(); // Запрашиваем следующий кадр
  });

  return null;
});

export default FloorCameraController;
